<template>
  <div class="gx-landing">
    <new-ui-nav :active="''" :is-logged-in="isLoggedIn" @home="goHome" @login="goLogin" @register="goRegister" @member="goMember" @about="goAbout" @enter="goEnter" />

    <!-- ── HERO ── -->
    <section class="gx-hero">
      <stars-canvas :count="110" :meteor-max="5" :meteor-gap="600" :opacity="1" :star-max-alpha="0.75" />
      <glow-orb color="rgba(212,169,70,.5)" :size="700" x="50%" y="38%" :op="0.055" />
      <glow-orb color="rgba(0,200,245,.4)" :size="560" x="72%" y="62%" :op="0.05" />
      <glow-orb color="rgba(124,58,237,.35)" :size="400" x="28%" y="66%" :op="0.04" />
      <div class="gx-hero__fade"></div>

      <div class="gx-hero__text" :style="heroTextStyle">
        <div class="gx-hero__eyebrow">
          <span class="gx-hero__eyebrow-line"></span>
          <span class="gx-hero__eyebrow-text">AI SMART OPERATIONS</span>
          <span class="gx-hero__eyebrow-line"></span>
        </div>
        <h1 class="gx-hero__title">观星树</h1>
        <div class="gx-hero__en">STAR &nbsp;&nbsp; TREE</div>
        <p class="gx-hero__slogan">AI智能店铺运营，致力店铺无人化</p>
        <p class="gx-hero__desc">从一粒种子，长成电商星辰大海中的参天大树</p>
      </div>

      <div class="gx-hero__tree-wrap" :style="heroTreeStyle">
        <div class="gx-hero__halo" :class="{ 'gx-hero__halo--tree': stage >= 3 }"></div>
        <div class="gx-hero__tree-float">
          <hero-tree :stage="stage" />
        </div>
      </div>

      <div class="gx-hero__scroll" :style="{ opacity: Math.max(0, 0.6 - scrollY / 160) }">
        <div class="gx-hero__scroll-box">
          <div class="gx-hero__scroll-dot"></div>
        </div>
        <span class="gx-hero__scroll-text">SCROLL</span>
      </div>
    </section>

    <!-- ── 星系列 ── -->
    <section v-reveal class="gx-series">
      <space-canvas :opacity="0.4" />
      <glow-orb :color="`${starSeries[activeSeries].color}55`" :size="560" x="50%" y="50%" :op="0.06" />
      <float-pts :count="8" />
      <div class="gx-section-inner">
        <section-head en="STAR SERIES" zh="星系列智能运营工具" sub="AI驱动，覆盖电商运营全链路，星系列矩阵全面进化" :visible="true" />
        <div class="gx-series__cards" @mouseenter="seriesPaused = true" @mouseleave="seriesPaused = false">
          <button
            v-for="(sp, i) in starSeries"
            :key="sp.id"
            class="gx-series__card"
            :class="{ 'gx-series__card--on': i === activeSeries }"
            :style="{ borderColor: i === activeSeries ? sp.color + '60' : 'rgba(255,255,255,.1)', boxShadow: i === activeSeries ? `0 12px 40px rgba(0,0,0,.4), 0 0 28px ${sp.color}22` : '0 4px 16px rgba(0,0,0,.22)' }"
            @click="selectSeries(i)"
          >
            <span v-if="i === activeSeries" class="gx-series__card-beam" :style="{ background: `linear-gradient(90deg,transparent,${sp.color},transparent)` }"></span>
            <span class="gx-series__card-icon" :style="{ background: sp.bg, boxShadow: i === activeSeries ? `0 0 22px ${sp.color}50` : 'none' }" v-html="sp.icon"></span>
            <span class="gx-series__card-name" :style="{ color: i === activeSeries ? '#D4A946' : '#FFFFFF' }">{{ sp.name }}</span>
            <span class="gx-series__card-sub">{{ sp.sub }}</span>
            <span v-if="i === activeSeries" class="gx-series__card-dot"></span>
          </button>
        </div>

        <div :key="`panel-${activeSeries}`" class="gx-series__panel" :style="{ borderColor: currentSeries.color + '50', boxShadow: `0 0 80px ${currentSeries.color}18, 0 24px 72px rgba(0,0,0,.55), inset 0 1px rgba(255,255,255,.09)` }" @mouseenter="seriesPaused = true" @mouseleave="seriesPaused = false">
          <span class="gx-series__panel-beam" :style="{ background: `linear-gradient(90deg,transparent,${currentSeries.color},transparent)` }"></span>
          <div class="gx-series__panel-left" :style="{ background: `linear-gradient(180deg,${currentSeries.color}0C,transparent)` }">
            <span v-if="currentSeries.badge" class="gx-series__badge" :class="{ 'gx-series__badge--flag': currentSeries.badge === 'FLAGSHIP' }" :style="currentSeries.badge === 'FLAGSHIP' ? badgeFlagStyle : badgeGhostStyle">{{ currentSeries.badge }}</span>
            <span class="gx-series__panel-icon" :style="{ background: currentSeries.bg, boxShadow: `0 0 40px ${currentSeries.color}45, 0 8px 28px rgba(0,0,0,.4)` }" v-html="currentSeries.icon"></span>
            <div>
              <div class="gx-series__panel-name" :style="{ textShadow: `0 0 30px ${currentSeries.color}55` }">{{ currentSeries.name }}</div>
              <div class="gx-series__panel-sub">{{ currentSeries.sub }}</div>
            </div>
          </div>
          <div class="gx-series__panel-right">
            <p class="gx-series__panel-desc">{{ currentSeries.desc }}</p>
            <div class="gx-series__tags">
              <span v-for="(tag, ti) in ['AI全链路', '智能化运营', '实时监控', '多平台支持']" :key="tag" class="gx-series__tag" :style="{ color: currentSeries.color, background: `${currentSeries.color}16`, borderColor: `${currentSeries.color}35` }">{{ tag }}</span>
            </div>
            <div v-if="currentSeries.id === 5" class="gx-series__soon">
              <span class="gx-series__soon-dot"></span>
              <span>即将上线</span>
            </div>
            <button v-else class="gx-series__enter" :style="{ background: `linear-gradient(135deg,${currentSeries.color},${currentSeries.color}cc)`, boxShadow: `0 4px 20px ${currentSeries.color}45` }">进入体验 →</button>
          </div>
        </div>

        <div class="gx-series__dots">
          <button
            v-for="(_, i) in starSeries"
            :key="i"
            class="gx-series__dot"
            :class="{ 'gx-series__dot--on': i === activeSeries }"
            @click="selectSeries(i)"
          ></button>
        </div>
      </div>
    </section>

    <!-- ── 十大功能 ── -->
    <section ref="featuresRef" class="gx-feats">
      <space-canvas :opacity="0.35" />
      <glow-orb color="rgba(0,200,245,.3)" :size="500" x="8%" y="52%" :op="0.06" />
      <glow-orb color="rgba(212,169,70,.25)" :size="440" x="94%" y="44%" :op="0.06" />
      <div class="gx-section-inner">
        <section-head en="PLATFORM FEATURES" zh="一棵树的生长，是十大功能的滋养" sub="从选品到售后，全链路AI智能化" ac="#00C8F5" :visible="featVisible" />

        <div class="gx-feats__layout" :class="{ 'gx-feats__layout--visible': featVisible }">
          <!-- 左侧功能列表 -->
          <div class="gx-feats__list">
            <div
              v-for="(f, i) in orbitFeats"
              :key="i"
              class="gx-feats__item"
              :class="{ 'gx-feats__item--on': i === activeFeat, 'gx-feats__item--hov': hovFeat === i }"
              @click="selectFeat(i)"
              @mouseenter="hovFeat = i"
              @mouseleave="hovFeat = -1"
            >
              <span class="gx-feats__item-icon" :class="{ 'gx-feats__item-icon--on': i === activeFeat }" v-html="orbitIcons[i]"></span>
              <span class="gx-feats__item-body">
                <span class="gx-feats__item-name" :class="{ 'gx-feats__item-name--on': i === activeFeat }">{{ f.name }}</span>
                <span class="gx-feats__item-sub">{{ f.sub }}</span>
              </span>
              <span v-if="i === activeFeat" class="gx-feats__item-dot"></span>
              <span v-if="i === activeFeat" class="gx-feats__item-progress">
                <span class="gx-feats__item-progress-fill" :style="{ width: cycleProgress * 100 + '%' }"></span>
              </span>
            </div>
          </div>

          <!-- 右侧详情面板 -->
          <div :key="`feat-${prevKey}-${activeFeat}`" class="gx-feats__panel">
            <div class="gx-feats__panel-head">
              <span class="gx-feats__panel-icon" v-html="orbitIcons[activeFeat]"></span>
              <span class="gx-feats__panel-body">
                <span class="gx-feats__panel-name">{{ currentFeat.name }}</span>
                <span class="gx-feats__panel-sub">{{ currentFeat.sub }}</span>
              </span>
              <span class="gx-feats__panel-live">
                <span class="gx-feats__panel-live-dot"></span>
                <span>LIVE DEMO</span>
              </span>
            </div>
            <div class="gx-feats__panel-body-wrap">
              <div class="gx-feats__pts">
                <div class="gx-feats__pts-label">核心功能</div>
                <div v-for="(pt, pi) in currentFeat.pts" :key="pt" class="gx-feats__pt" :style="{ animation: `gx-revealUp .4s ${pi * 0.07}s both` }">
                  <span class="gx-feats__pt-check" :style="{ animation: `gx-checkPop .4s ${pi * 0.07 + 0.08}s ease-out both` }">✓</span>
                  <span class="gx-feats__pt-text">{{ pt }}</span>
                </div>
              </div>
              <div class="gx-feats__preview">
                <component :is="currentPreview" :run="featPreviewRun" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── 星人在成长 ── -->
    <section ref="communityRef" class="gx-community">
      <space-canvas :opacity="0.4" />
      <glow-orb color="rgba(0,200,245,.3)" :size="520" x="88%" y="40%" :op="0.07" />
      <glow-orb color="rgba(212,169,70,.3)" :size="460" x="12%" y="60%" :op="0.06" />
      <float-pts :count="8" />
      <div class="gx-section-inner">
        <section-head en="STAR COMMUNITY" zh="星人在成长" sub="每一颗星，都是一个正在成长的店铺" ac="#00C8F5" :visible="commVisible" />

        <!-- 成长四阶段 -->
        <div class="gx-community__journey" :style="{ opacity: commVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(.22,1,.36,1) 0.2s' }">
          <div v-for="(s, i) in journeyStages" :key="i" class="gx-community__stage">
            <div
              class="gx-community__stage-card"
              :class="{ 'gx-community__stage-card--on': i === journeyStep, 'gx-community__stage-card--done': i < journeyStep }"
              :style="{
                background: i === journeyStep ? `${s.color}12` : i < journeyStep ? `${s.color}08` : 'rgba(255,255,255,.04)',
                borderColor: i === journeyStep ? s.color + '60' : i < journeyStep ? s.color + '30' : 'rgba(255,255,255,.08)'
              }"
            >
              <div class="gx-community__stage-icon" :style="{ animation: i === journeyStep ? 'gx-journeyPulse 2s ease-in-out infinite' : 'none', filter: i === journeyStep ? `drop-shadow(0 0 10px ${s.color}88)` : 'none' }">{{ s.icon }}</div>
              <div class="gx-community__stage-name" :style="{ color: i === journeyStep ? s.color : i < journeyStep ? '#F0EBE0' : '#8A95B0' }">{{ s.label }}</div>
              <div class="gx-community__stage-day" :style="{ color: i === journeyStep ? s.color : '#8A95B0' }">{{ s.day }}</div>
              <div class="gx-community__stage-metric" :style="{ color: i === journeyStep ? s.color : i < journeyStep ? 'rgba(220,215,240,.7)' : 'rgba(220,215,240,.35)' }">{{ s.metric }}</div>
              <div v-if="i === journeyStep" class="gx-community__stage-dot" :style="{ background: s.color, boxShadow: `0 0 10px ${s.color}` }"></div>
            </div>
            <span v-if="i < journeyStages.length - 1" class="gx-community__stage-arrow" :style="{ color: i < journeyStep ? 'rgba(212,169,70,.7)' : 'rgba(255,255,255,.2)' }">→</span>
          </div>
        </div>

        <!-- 增长曲线 + 实时动态 -->
        <div class="gx-community__split" :style="{ opacity: commVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(.22,1,.36,1) 0.3s' }">
          <div class="gx-community__chart">
            <div class="gx-community__chart-label">用户平均GMV增长趋势（万元/月）</div>
            <svg width="100%" viewBox="0 0 500 130" style="overflow: visible; display: block">
              <defs>
                <linearGradient id="gx-growth-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="#D4A946" stop-opacity=".35" />
                  <stop offset="100%" stop-color="#D4A946" stop-opacity="0" />
                </linearGradient>
                <clipPath id="gx-chartClip">
                  <rect x="36" y="0" :width="commVisible ? 452 : 0" height="130" style="transition: width 2.8s ease-out" />
                </clipPath>
              </defs>
              <g v-for="val in [50, 100, 150, 200]" :key="val">
                <line x1="36" :y1="102 - (val / 220) * 90" x2="488" :y2="102 - (val / 220) * 90" stroke="rgba(255,255,255,.06)" stroke-width="1" />
              </g>
              <path :d="growthArea" fill="url(#gx-growth-grad)" clip-path="url(#gx-chartClip)" />
              <path :d="growthLine" fill="none" stroke="#D4A946" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" clip-path="url(#gx-chartClip)" />
              <circle v-for="(p, i) in growthPts" :key="i" v-show="commVisible && i % 3 === 0" :cx="p.x" :cy="p.y" r="4" fill="#D4A946" style="animation: gx-checkPop .3s both" />
              <text v-for="(l, j) in growthLabels" :key="l" :x="36 + (j * 2 / 11) * 452" y="118" fill="rgba(200,192,224,.38)" font-size="8" text-anchor="middle">{{ l }}</text>
            </svg>
          </div>

          <div class="gx-community__feed">
            <div class="gx-community__feed-head">
              <span class="gx-community__feed-dot"></span>
              实时运营动态
            </div>
            <div class="gx-community__feed-list">
              <div
                v-for="(item, i) in visibleFeed"
                :key="`${feedIdx}-${i}`"
                class="gx-community__feed-item"
                :style="{ animation: `gx-feedScroll 2.4s ${i * 0.05}s ease-in-out infinite` }"
              >
                <div class="gx-community__feed-top">
                  <span class="gx-community__feed-store">{{ item.store }}</span>
                  <span class="gx-community__feed-green"></span>
                </div>
                <div class="gx-community__feed-action">{{ item.action }}</div>
                <div class="gx-community__feed-result">{{ item.result }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 四项数据 -->
        <div class="gx-community__stats">
          <div
            v-for="(s, i) in cstats"
            :key="s.label"
            class="gx-community__stat"
            :class="{ 'gx-community__stat--hov': hovStat === i }"
            @mouseenter="hovStat = i"
            @mouseleave="hovStat = -1"
            :style="statStyle(s, i)"
          >
            <span class="gx-community__stat-p1" :style="{ background: s.color, boxShadow: `0 0 6px ${s.color}` }"></span>
            <span class="gx-community__stat-p2" :style="{ background: s.color }"></span>
            <span class="gx-community__stat-p3" :style="{ background: s.color }"></span>
            <span class="gx-community__stat-beam" :style="{ background: `linear-gradient(90deg,transparent,${s.color}CC,transparent)` }"></span>
            <div class="gx-community__stat-orb">
              <span class="gx-community__stat-ring" :style="{ borderColor: s.color + '30', borderTopColor: s.color + '90' }"></span>
              <span class="gx-community__stat-ring-inner" :style="{ borderColor: s.color + '25' }"></span>
              <span class="gx-community__stat-icon" :style="statIconStyle(s, i)" v-html="cstatIcons[i]"></span>
            </div>
            <div class="gx-community__stat-num" :style="statNumStyle(s, i)">{{ statDisplay(s, i) }}</div>
            <div class="gx-community__stat-divider" :style="{ background: `linear-gradient(90deg,transparent,${s.color}70,transparent)` }"></div>
            <div class="gx-community__stat-label" :style="{ color: hovStat === i ? '#F0EBE0' : 'rgba(220,215,240,.88)' }">{{ s.label }}</div>
            <div class="gx-community__stat-sub" :style="{ color: hovStat === i ? s.color + 'BB' : 'rgba(160,152,190,.5)' }">{{ s.sub }}</div>
          </div>
        </div>
      </div>
    </section>

    <new-ui-footer @member="goMember" @about="goAbout" @service="goService" />
  </div>
</template>

<script>
import StarsCanvas from '../../components/newUi/StarsCanvas.vue'
import SpaceCanvas from '../../components/newUi/SpaceCanvas.vue'
import GlowOrb from '../../components/newUi/GlowOrb.vue'
import FloatPts from '../../components/newUi/FloatPts.vue'
import SectionHead from '../../components/newUi/SectionHead.vue'
import NewUiNav from '../../components/newUi/NewUiNav.vue'
import NewUiFooter from '../../components/newUi/NewUiFooter.vue'
import HeroTree from '../../components/newUi/HeroTree.vue'
import PrevAgent from '../../components/newUi/previews/PrevAgent.vue'
import PrevStores from '../../components/newUi/previews/PrevStores.vue'
import PrevSearch from '../../components/newUi/previews/PrevSearch.vue'
import PrevCloud from '../../components/newUi/previews/PrevCloud.vue'
import PrevDetect from '../../components/newUi/previews/PrevDetect.vue'
import PrevDistrib from '../../components/newUi/previews/PrevDistrib.vue'
import PrevOptimize from '../../components/newUi/previews/PrevOptimize.vue'
import PrevAIImage from '../../components/newUi/previews/PrevAIImage.vue'
import PrevAnalytics from '../../components/newUi/previews/PrevAnalytics.vue'
import PrevOrders from '../../components/newUi/previews/PrevOrders.vue'

const STAR_ICONS = {
  tree: '<img class="gx-series__brand-logo" src="https://xinghaohuo.oss-cn-beijing.aliyuncs.com/logo.png" alt="观星树 Logo">',
  goods: '<svg width="36" height="36" viewBox="0 0 32 32" fill="none"><path d="M5 11h22l-2.5 14.5H7.5L5 11z" stroke="#fff" stroke-width="1.8" stroke-linejoin="round" fill="none"/><path d="M12 11V9a4 4 0 018 0v2" stroke="#fff" stroke-width="1.8" stroke-linecap="round"/><circle cx="12" cy="18" r="1.5" fill="#fff" opacity=".8"/><circle cx="20" cy="18" r="1.5" fill="#fff" opacity=".8"/></svg>',
  detect: '<svg width="36" height="36" viewBox="0 0 32 32" fill="none"><path d="M16 3L3 8v9c0 7 5.5 12.5 13 13.5C24.5 29.5 30 24 30 17V8L16 3z" stroke="#fff" stroke-width="1.8" stroke-linejoin="round" fill="none"/><path d="M11 16.5l3.5 3.5 7-7" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  group: '<svg width="36" height="36" viewBox="0 0 32 32" fill="none"><path d="M16 4l-3 8H5l6 5-2.5 8L16 20l7.5 5L21 17l6-5h-8z" stroke="#fff" stroke-width="1.8" stroke-linejoin="round" fill="none"/></svg>',
  more: '<svg width="36" height="36" viewBox="0 0 32 32" fill="none"><circle cx="10" cy="16" r="3.5" fill="#fff" opacity=".35"/><circle cx="16" cy="16" r="3.5" fill="#fff" opacity=".8"/><circle cx="22" cy="16" r="3.5" fill="#fff" opacity=".35"/></svg>'
}

export default {
  name: 'NewLandingPage',
  components: {
    StarsCanvas,
    SpaceCanvas,
    GlowOrb,
    FloatPts,
    SectionHead,
    NewUiNav,
    NewUiFooter,
    HeroTree,
    PrevAgent,
    PrevStores,
    PrevSearch,
    PrevCloud,
    PrevDetect,
    PrevDistrib,
    PrevOptimize,
    PrevAIImage,
    PrevAnalytics,
    PrevOrders
  },
  data() {
    return {
      mounted: false,
      stage: 0,
      stageTimer: null,
      scrollY: 0,
      seriesTimer: null,
      activeSeries: 0,
      seriesPaused: false,
      starSeries: [
        { id: 1, name: '观星树', sub: 'AI全链路智能运营', desc: '完整AI运营平台，覆盖选品到售后全链路，是电商运营的核心引擎', badge: 'FLAGSHIP', color: '#D4A946', bg: '', icon: STAR_ICONS.tree },
        { id: 2, name: '星好货', sub: '智能选品·高效运营', desc: '优质撮合供应链，精准找到爆款，助力店铺快速起盘', badge: null, color: '#8B5CF6', bg: 'linear-gradient(135deg,#2d1b5e,#5B21B6)', icon: STAR_ICONS.goods },
        { id: 3, name: '星检测', sub: 'AI智能识别违规', desc: '四维检测合规护航，守护店铺全天候安全，检测准确率99.99%', badge: null, color: '#E85D5D', bg: 'linear-gradient(135deg,#4a1010,#8B2020)', icon: STAR_ICONS.detect },
        { id: 4, name: '星群控', sub: '多店批量群控管理', desc: '多店统一操作，一键批量管理，提升10倍运营效率', badge: null, color: '#00C8F5', bg: 'linear-gradient(135deg,#0d2a3a,#0e5a80)', icon: STAR_ICONS.group },
        { id: 5, name: '更多产品', sub: 'AI驱动，即将上线', desc: '更多星系列工具持续进化中，敬请期待全新体验', badge: 'COMING SOON', color: '#8A95B0', bg: 'linear-gradient(135deg,#1a2030,#28304a)', icon: STAR_ICONS.more }
      ],
      orbitFeats: [
        { id: 1, name: 'Agent自动化', sub: 'AI智能体·全程无人化', pts: ['智能任务编排', '自动巡店执行', 'AI策略决策', '24小时无人值守'] },
        { id: 2, name: '店铺管理', sub: '多平台授权·分组管理', pts: ['多平台统一授权', '店铺分组运营', '铺货额度监控', '会员续订操作'] },
        { id: 3, name: '选品大厅', sub: '1688超级搜索·AI选品', pts: ['1688超级搜索', 'AI智能选品', '批量关键词抓取', '以图搜货'] },
        { id: 4, name: '云商品库', sub: '商品中台·AI裂变', pts: ['商品中台管理', 'AI一键裂变', 'SKU批量替换', '多平台同步'] },
        { id: 5, name: 'AI检测', sub: '四维检测·合规护航', pts: ['图片违规检测', '标题关键词检测', 'SKU检测', 'AI鉴图'] },
        { id: 6, name: '智能铺货', sub: '盲盒铺货·策略上架', pts: ['盲盒自动铺货', '手动勾选铺货', '上架策略配置', '发货策略管理'] },
        { id: 7, name: '商品优化', sub: 'AI标题·主图·视频', pts: ['AI标题生成', 'AI主图优化', 'AI白底图', 'AI视频制作'] },
        { id: 8, name: 'AI生成图', sub: 'AI图片生成·智能创作', pts: ['AI商品图生成', '风格一键转换', '背景智能替换', '批量处理图片'] },
        { id: 9, name: '数据分析', sub: '店铺看板·商品看板', pts: ['店铺数据看板', '商品销售分析', '27项可配置指标', '数据驱动决策'] },
        { id: 10, name: '订单售后', sub: '订单·售后·采购', pts: ['订单统一管理', '售后自动处理', '采购管理', '报销流程简化'] }
      ],
      orbitIcons: [
        '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="4" y="7" width="14" height="11" rx="3" stroke="#D4A946" stroke-width="1.5"/><circle cx="8" cy="12" r="1.5" fill="#D4A946" opacity=".8"/><circle cx="14" cy="12" r="1.5" fill="#D4A946" opacity=".8"/><path d="M8 7V5.5a3 3 0 016 0V7" stroke="#D4A946" stroke-width="1.4"/><path d="M11 4v-2" stroke="#D4A946" stroke-width="1.3" stroke-linecap="round" opacity=".5"/><path d="M3 18l2-1M19 18l-2-1" stroke="#D4A946" stroke-width="1.2" stroke-linecap="round" opacity=".55"/></svg>',
        '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="2" y="8" width="18" height="12" rx="2" stroke="#D4A946" stroke-width="1.5"/><path d="M2 8l2-5h14l2 5" stroke="#D4A946" stroke-width="1.5" stroke-linejoin="round"/><rect x="7" y="12" width="8" height="8" rx="1" stroke="#D4A946" stroke-width="1.2"/></svg>',
        '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="10" cy="10" r="6.5" stroke="#D4A946" stroke-width="1.5"/><path d="M15 15l4 4" stroke="#D4A946" stroke-width="1.8" stroke-linecap="round"/><path d="M7.5 10h5M10 7.5v5" stroke="#D4A946" stroke-width="1.2" stroke-linecap="round" opacity=".7"/></svg>',
        '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M5 14a4 4 0 01-.5-7.94A5 5 0 0115 9h.5a3 3 0 010 6H5z" stroke="#D4A946" stroke-width="1.5" stroke-linecap="round"/><path d="M11 12v5M8.5 14.5l2.5 2.5 2.5-2.5" stroke="#D4A946" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" opacity=".75"/></svg>',
        '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M11 2L3 5.5v7c0 5 3.5 8.5 8 9.5 4.5-1 8-4.5 8-9.5v-7L11 2z" stroke="#D4A946" stroke-width="1.5" stroke-linejoin="round"/><path d="M7.5 11.5l2.5 2.5 5-5" stroke="#D4A946" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M3 8l8-5 8 5v10l-8 3-8-3V8z" stroke="#D4A946" stroke-width="1.5" stroke-linejoin="round"/><path d="M11 3v16M3 8l8 3 8-3" stroke="#D4A946" stroke-width="1.2" stroke-linejoin="round" opacity=".65"/></svg>',
        '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="11" r="4" stroke="#D4A946" stroke-width="1.5"/><path d="M11 2v2M11 18v2M2 11h2M18 11h2M4.5 4.5l1.4 1.4M16.1 16.1l1.4 1.4M4.5 17.5l1.4-1.4M16.1 5.9l1.4-1.4" stroke="#D4A946" stroke-width="1.4" stroke-linecap="round" opacity=".6"/><circle cx="11" cy="11" r="1.5" fill="#D4A946"/></svg>',
        '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="2" y="4" width="18" height="14" rx="2" stroke="#D4A946" stroke-width="1.5"/><path d="M2 14l5-5 4 4 3-3 6 6" stroke="#D4A946" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" opacity=".7"/><circle cx="16" cy="8" r="1.5" fill="#D4A946" opacity=".7"/><path d="M13 2l1 2M17 3l-1 2" stroke="#D4A946" stroke-width="1.2" stroke-linecap="round" opacity=".5"/></svg>',
        '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="2" y="12" width="4" height="8" rx="1" stroke="#D4A946" stroke-width="1.4"/><rect x="9" y="7" width="4" height="13" rx="1" stroke="#D4A946" stroke-width="1.4"/><rect x="16" y="3" width="4" height="17" rx="1" stroke="#D4A946" stroke-width="1.4"/><path d="M2 21h18" stroke="#D4A946" stroke-width="1.1" opacity=".5"/></svg>',
        '<svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="2" y="5" width="18" height="13" rx="2" stroke="#D4A946" stroke-width="1.5"/><path d="M2 9h18" stroke="#D4A946" stroke-width="1.4"/><rect x="5" y="13" width="5" height="2" rx="1" fill="#D4A946" opacity=".6"/><rect x="12" y="13" width="3" height="2" rx="1" fill="#D4A946" opacity=".4"/></svg>'
      ],
      activeFeat: 0,
      hovFeat: -1,
      prevKey: 0,
      cycleMs: 0,
      featRaf: 0,
      featTimer: null,
      featVisible: false,
      featPreviewRun: false,
      cstatIcons: [
        '<svg width="28" height="28" viewBox="0 0 28 28" fill="none"><circle cx="14" cy="9" r="5" stroke="#D4A946" stroke-width="1.8"/><path d="M4 24c0-5.523 4.477-10 10-10s10 4.477 10 10" stroke="#D4A946" stroke-width="1.8" stroke-linecap="round"/><circle cx="21" cy="8" r="2.5" stroke="#D4A946" stroke-width="1.4" opacity=".5"/><path d="M24 5v6M21 8h6" stroke="#D4A946" stroke-width="1.4" stroke-linecap="round" opacity=".5"/></svg>',
        '<svg width="28" height="28" viewBox="0 0 28 28" fill="none"><rect x="5" y="10" width="18" height="14" rx="3" stroke="#00C8F5" stroke-width="1.8"/><path d="M9 10V8a5 5 0 0110 0v2" stroke="#00C8F5" stroke-width="1.8" stroke-linecap="round"/><path d="M14 15v4M12 17h4" stroke="#00C8F5" stroke-width="1.6" stroke-linecap="round" opacity=".7"/><circle cx="20" cy="8" r="4" fill="#0D1630" stroke="#00C8F5" stroke-width="1.4"/><path d="M18 8l1.5 1.5L22 6" stroke="#00C8F5" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        '<svg width="28" height="28" viewBox="0 0 28 28" fill="none"><path d="M14 3L5 7v8c0 5 4 9.4 9 10.5C19 24.4 23 20 23 15V7L14 3z" stroke="#7C3AED" stroke-width="1.8" stroke-linejoin="round"/><path d="M9.5 14.5l3 3 6-6" stroke="#7C3AED" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        '<svg width="28" height="28" viewBox="0 0 28 28" fill="none"><rect x="4" y="4" width="9" height="9" rx="2.5" stroke="#9B8AFB" stroke-width="1.7"/><rect x="15" y="4" width="9" height="9" rx="2.5" stroke="#9B8AFB" stroke-width="1.7"/><rect x="4" y="15" width="9" height="9" rx="2.5" stroke="#9B8AFB" stroke-width="1.7"/><rect x="15" y="15" width="9" height="9" rx="2.5" stroke="#9B8AFB" stroke-width="1.7" opacity=".45" stroke-dasharray="2 2"/><circle cx="19.5" cy="19.5" r="2" fill="#9B8AFB" opacity=".6"/></svg>'
      ],
      cstats: [
        { target: 10000, suffix: '+', label: '星人用户', sub: '活跃运营人数', color: '#D4A946' },
        { target: 1, suffix: '亿+', label: '商品入库', sub: '云商品库总量', color: '#00C8F5' },
        { target: 9999, display: '99.99%', label: '检测准确率', sub: 'AI四维检测', color: '#7C3AED' },
        { target: 100, suffix: '+', label: '覆盖类目', sub: '全品类覆盖', color: '#9B8AFB' }
      ],
      statCounts: [0, 0, 0, 0],
      hovStat: -1,
      journeyStages: [
        { icon: '🌱', label: '接入观星树', day: 'Day 0', metric: '开启智能化运营', color: '#4CAF7A' },
        { icon: '⚡', label: 'AI全面优化', day: 'Day 7', metric: '运营效率 ×3', color: '#D4A946' },
        { icon: '📈', label: '数据快速增长', day: 'Day 30', metric: 'GMV +156%', color: '#00C8F5' },
        { icon: '🌳', label: '突破新高度', day: 'Day 90', metric: '月销突破10万+', color: '#E8B950' }
      ],
      journeyStep: 0,
      journeyTimer: null,
      liveFeed: [
        { store: '观星树小店', action: '完成AI标题优化', result: '86款 · 转化率+23%' },
        { store: '观星树旗舰', action: '智能铺货至7平台', result: '234款 · 省时2小时' },
        { store: '观星树精品铺', action: 'AI检测拦截违规', result: '12项风险 · 已修复' },
        { store: '观星树好物汇', action: 'AI生成商品主图', result: '50张 · 品质↑3星' },
        { store: '观星树潮品店', action: '订单自动处理', result: '238单 · 售后0纠纷' },
        { store: '观星树数码馆', action: '盲盒铺货完成', result: '1200款 · 7平台同步' },
        { store: '观星树优选铺', action: '数据分析报告', result: 'GMV月环比+156%' },
        { store: '观星树时尚馆', action: 'Agent自动巡店', result: '5项异常 · 已处理' }
      ],
      feedIdx: 0,
      feedTimer: null,
      commVisible: false,
      growthData: [3, 5, 8, 13, 21, 34, 55, 89, 110, 145, 180, 220],
      growthLabels: ['1月', '3月', '5月', '7月', '9月', '11月']
    }
  },
  computed: {
    isLoggedIn() {
      return !!this.$store.state.login.token
    },
    currentSeries() {
      return this.starSeries[this.activeSeries]
    },
    currentFeat() {
      return this.orbitFeats[this.activeFeat]
    },
    currentPreview() {
      return ['PrevAgent', 'PrevStores', 'PrevSearch', 'PrevCloud', 'PrevDetect', 'PrevDistrib', 'PrevOptimize', 'PrevAIImage', 'PrevAnalytics', 'PrevOrders'][this.activeFeat]
    },
    heroTextStyle() {
      return {
        opacity: Math.max(0, 1 - this.scrollY / 500),
        transform: `translate(-50%, ${this.scrollY * 0.35}px)`
      }
    },
    heroTreeStyle() {
      return {
        transform: `translate(-50%, ${this.scrollY * 0.06}px)`,
        opacity: Math.max(0, 1 - this.scrollY / 500)
      }
    },
    cycleProgress() {
      return Math.min(this.cycleMs / 4500, 1)
    },
    visibleFeed() {
      return [
        this.liveFeed[this.feedIdx % this.liveFeed.length],
        this.liveFeed[(this.feedIdx + 1) % this.liveFeed.length],
        this.liveFeed[(this.feedIdx + 2) % this.liveFeed.length]
      ]
    },
    growthPts() {
      return this.growthData.map((d, i) => ({
        x: 36 + (i / 11) * 452,
        y: 102 - (d / 220) * 90
      }))
    },
    growthLine() {
      return this.growthPts.map((p, i) => {
        if (i === 0) return `M${p.x.toFixed(1)},${p.y.toFixed(1)}`
        const prev = this.growthPts[i - 1]
        const cpx = (prev.x + p.x) / 2
        return `C${cpx.toFixed(1)},${prev.y.toFixed(1)} ${cpx.toFixed(1)},${p.y.toFixed(1)} ${p.x.toFixed(1)},${p.y.toFixed(1)}`
      }).join(' ')
    },
    growthArea() {
      const last = this.growthPts[this.growthPts.length - 1]
      return `${this.growthLine} L${last.x},102 L36,102 Z`
    },
    badgeFlagStyle() {
      return { background: 'linear-gradient(135deg,#D4A946,#F0C44A)', color: '#0D1630', animation: 'gx-flagShimmer 2.5s ease-in-out infinite' }
    },
    badgeGhostStyle() {
      return { background: 'rgba(255,255,255,.08)', color: '#8A95B0', border: '1px solid rgba(255,255,255,.15)' }
    }
  },
  mounted() {
    this.mounted = true
    const query = this.$route.query
    if (query.inviteCode) {
      localStorage['inviteCode'] = String(query.inviteCode)
      this.$router.replace('/register')
      return
    } else if (query.invite_code) {
      localStorage['inviteCode'] = String(query.invite_code)
      this.$router.replace('/register')
      return
    }
    this.$nextTick(() => {
      const DURS = [2800, 2600, 2600, 2600, 5200]
      const runStage = () => {
        this.stage = (this.stage + 1) % 5
        this.stageTimer = setTimeout(runStage, DURS[this.stage])
      }
      this.stageTimer = setTimeout(runStage, DURS[0])
    })
    const fn = () => { this.scrollY = window.scrollY }
    window.addEventListener('scroll', fn, { passive: true })
    this._scrollFn = fn

    // 星系列自动轮播
    this.seriesTimer = setInterval(() => {
      if (!this.seriesPaused) this.activeSeries = (this.activeSeries + 1) % this.starSeries.length
    }, 3400)

    // 十大功能在首次进入视口后才开始轮播和演示
    this.initFeaturesObserver()
    this.startJourney()
    this.startFeed()

    // 社区区块 reveal
    this.initCommunityObserver()
  },
  beforeDestroy() {
    if (this.stageTimer) clearTimeout(this.stageTimer)
    if (this.seriesTimer) clearInterval(this.seriesTimer)
    if (this.featTimer) clearInterval(this.featTimer)
    cancelAnimationFrame(this.featRaf)
    if (this.journeyTimer) clearInterval(this.journeyTimer)
    if (this.feedTimer) clearInterval(this.feedTimer)
    window.removeEventListener('scroll', this._scrollFn)
    if (this._featObs) this._featObs.disconnect()
    if (this._commObs) this._commObs.disconnect()
  },
  methods: {
    startFeatCycle() {
      if (this.featTimer) return
      const CYCLE = 4500
      this.featTimer = setInterval(() => {
        this.activeFeat = (this.activeFeat + 1) % 10
        this.prevKey += 1
        this.restartFeatPreview()
      }, CYCLE)
      const start = performance.now()
      const tick = (now) => {
        this.cycleMs = (now - start) % CYCLE
        this.featRaf = requestAnimationFrame(tick)
      }
      this.featRaf = requestAnimationFrame(tick)
    },
    initFeaturesObserver() {
      const el = this.$refs.featuresRef
      if (!el) return
      const reveal = () => {
        if (this.featVisible) return
        this.featVisible = true
        this.prevKey += 1
        this.startFeatCycle()
        this.restartFeatPreview()
        if (this._featObs) this._featObs.disconnect()
        this._featObs = null
      }
      if (!window.IntersectionObserver) {
        reveal()
        return
      }
      const obs = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) reveal()
        })
      }, { threshold: 0.1 })
      obs.observe(el)
      this._featObs = obs
    },
    restartFeatPreview() {
      if (!this.featVisible) return
      this.featPreviewRun = false
      this.$nextTick(() => {
        if (this.featVisible) this.featPreviewRun = true
      })
    },
    startJourney() {
      this.journeyTimer = setInterval(() => {
        this.journeyStep = (this.journeyStep + 1) % this.journeyStages.length
      }, 2600)
    },
    startFeed() {
      this.feedTimer = setInterval(() => {
        this.feedIdx = (this.feedIdx + 1) % this.liveFeed.length
      }, 2400)
    },
    initCommunityObserver() {
      const el = this.$refs.communityRef
      if (!el) return
      const obs = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.commVisible = true
            this.runCountUp()
            obs.disconnect()
          }
        })
      }, { threshold: 0.1 })
      obs.observe(el)
      this._commObs = obs
    },
    runCountUp() {
      const dur = 2200
      const t0 = performance.now()
      const tick = (now) => {
        const p = Math.min((now - t0) / dur, 1)
        const ease = 1 - Math.pow(1 - p, 3)
        this.statCounts = this.cstats.map((s) => Math.round(ease * s.target))
        if (p < 1) requestAnimationFrame(tick)
      }
      requestAnimationFrame(tick)
    },
    statDisplay(s, i) {
      if (s.display) return s.display
      return `${this.statCounts[i].toLocaleString()}${s.suffix || ''}`
    },
    statStyle(s, i) {
      const hov = this.hovStat === i
      return {
        background: hov ? `linear-gradient(160deg,${s.color}0D 0%,rgba(13,22,48,.85) 100%)` : 'rgba(255,255,255,.042)',
        borderColor: hov ? s.color + '55' : 'rgba(255,255,255,.09)',
        boxShadow: hov ? `0 24px 72px rgba(0,0,0,.6), 0 0 60px ${s.color}18, inset 0 1px rgba(255,255,255,.08)` : '0 4px 32px rgba(0,0,0,.38), inset 0 1px rgba(255,255,255,.05)',
        transform: hov ? 'translateY(-8px)' : 'none',
        opacity: this.commVisible ? 1 : 0,
        transition: `all .65s cubic-bezier(.22,1,.36,1) ${i * 0.12}s`
      }
    },
    statIconStyle(s, i) {
      const hov = this.hovStat === i
      return {
        background: hov ? `radial-gradient(circle,${s.color}28 0%,${s.color}0A 100%)` : `${s.color}10`,
        borderColor: s.color + '45',
        boxShadow: hov ? `0 0 40px ${s.color}60, inset 0 0 20px ${s.color}18` : `0 0 18px ${s.color}28`
      }
    },
    statNumStyle(s, i) {
      const hov = this.hovStat === i
      return {
        background: `linear-gradient(135deg,#F0EBE0 0%,${s.color} 45%,#F0EBE0 100%)`,
        backgroundSize: '200% 100%',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        filter: hov ? `drop-shadow(0 0 12px ${s.color}80)` : `drop-shadow(0 0 6px ${s.color}40)`
      }
    },
    selectSeries(i) {
      this.activeSeries = i
    },
    selectFeat(i) {
      this.activeFeat = i
      this.prevKey += 1
      this.restartFeatPreview()
    },
    goHome() { this.$router.push('/') },
    goLogin() { this.$router.push('/login') },
    goRegister() { this.$router.push('/register') },
    goEnter() {
      if (this.isLoggedIn) this.$router.push('/ucenter?menuId=113')
      else this.$router.push('/login')
    },
    goMember() { this.$router.push('/vip') },
    goAbout() { this.$router.push('/about') },
    goService() {
      if (this.isLoggedIn) this.$router.push('/notification')
    }
  }
}
</script>

<style scoped>
.gx-landing {
  min-height: 100vh;
  background: #070E22;
  overflow-x: hidden;
  position: relative;
  font-family: 'Noto Sans SC', sans-serif;
  overflow-y: hidden;
}
.gx-section-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 52px;
  position: relative;
  z-index: 2;
}

/* ── HERO ── */
.gx-hero {
  position: relative;
  height: 100vh;
  min-height: 820px;
  overflow: hidden;
  background: linear-gradient(155deg, #070E22 0%, #0D1630 35%, #152248 58%, #0D1630 80%, #070E22 100%);
}
.gx-hero__fade {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: linear-gradient(to top, #070E22 0%, transparent 100%);
  pointer-events: none;
  z-index: 3;
}
.gx-hero__text {
  position: absolute;
  top: 8%;
  left: 50%;
  z-index: 10;
  text-align: center;
  width: 100%;
  max-width: 760px;
  padding: 0 28px;
  pointer-events: none;
}
.gx-hero__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 20px;
  animation: gx-heroWord .7s .05s both;
}
.gx-hero__eyebrow-line {
  width: 24px;
  height: 1px;
}
.gx-hero__eyebrow-line:first-child { background: linear-gradient(90deg, transparent, #D4A946); }
.gx-hero__eyebrow-line:last-child { background: linear-gradient(90deg, #D4A946, transparent); }
.gx-hero__eyebrow-text {
  font-family: Inter, monospace;
  font-size: 10px;
  color: #D4A946;
  letter-spacing: 4.5px;
  font-weight: 700;
}
.gx-hero__title {
  font-family: 'Noto Serif SC', serif;
  font-size: clamp(80px, 10vw, 120px);
  font-weight: 700;
  margin: 0 0 5px;
  line-height: 1;
  letter-spacing: .05em;
  background: linear-gradient(140deg, #D4A946 0%, #F0C44A 40%, #FFF9E4 55%, #F0C44A 72%, #D4A946 100%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gx-shimmer 5s linear infinite, gx-heroWord .85s .08s both;
}
.gx-hero__en {
  font-family: Inter, monospace;
  font-size: 13px;
  color: rgba(200, 192, 224, .4);
  letter-spacing: 7px;
  margin-bottom: 20px;
  animation: gx-heroWord .7s .22s both;
}
.gx-hero__slogan {
  font-size: clamp(16px, 1.8vw, 20px);
  font-weight: 600;
  color: #00C8F5;
  margin: 0 0 8px;
  letter-spacing: .03em;
  animation: gx-heroWord .7s .32s both;
}
.gx-hero__desc {
  font-size: 15px;
  color: rgba(220, 215, 240, .6);
  margin: 0 0 36px;
  line-height: 1.75;
  animation: gx-heroWord .7s .42s both;
}
.gx-hero__tree-wrap {
  position: absolute;
  bottom: 3%;
  left: 50%;
  z-index: 5;
}
.gx-hero__halo {
  position: absolute;
  width: 120%;
  height: 120%;
  border-radius: 50%;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, rgba(0, 200, 245, .07) 0%, transparent 68%);
  animation: gx-canopyBreathe 4s ease-in-out infinite;
  pointer-events: none;
  transition: background 1.8s ease;
}
.gx-hero__halo--tree {
  background: radial-gradient(circle, rgba(212, 169, 70, .1) 0%, transparent 68%);
}
.gx-hero__tree-float {
  animation: gx-floatY 5s ease-in-out infinite;
}
.gx-hero__scroll {
  position: absolute;
  bottom: 22px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  pointer-events: none;
  z-index: 15;
}
.gx-hero__scroll-box {
  width: 22px;
  height: 36px;
  border-radius: 11px;
  border: 1.5px solid rgba(212, 169, 70, .3);
  display: flex;
  justify-content: center;
  padding: 4px 0;
}
.gx-hero__scroll-dot {
  width: 2.5px;
  height: 9px;
  border-radius: 2px;
  background: #D4A946;
  animation: gx-floatY 1.8s ease-in-out infinite;
}
.gx-hero__scroll-text {
  font-family: Inter, monospace;
  font-size: 8px;
  color: rgba(200, 192, 224, .28);
  letter-spacing: 2.5px;
}
/* ── 星系列 ── */
.gx-series {
  padding: 140px 0 130px;
  background: linear-gradient(180deg, #070E22 0%, #0D1630 40%, #152248 65%, #0D1630 100%);
  position: relative;
  overflow: hidden;
}
.gx-series__cards {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
  margin-bottom: 20px;
}
.gx-series__card {
  border-radius: 18px;
  padding: 18px 14px;
  cursor: pointer;
  position: relative;
  background: rgba(255, 255, 255, .05);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1.5px solid rgba(255, 255, 255, .1);
  text-align: center;
  outline: none;
  transition: all 0.5s cubic-bezier(.22, 1, .36, 1);
}
.gx-series__card:hover {
  background: rgba(255, 255, 255, .1);
  border-color: rgba(212, 169, 70, .45);
  transform: translateY(-3px);
}
.gx-series__card--on {
  background: rgba(255, 255, 255, .12);
  transform: translateY(-5px);
}
.gx-series__card-beam {
  position: absolute;
  top: 0;
  left: 20%;
  right: 20%;
  height: 2px;
  border-radius: 1px;
}
.gx-series__card-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
  transition: all 0.4s cubic-bezier(.22, 1, .36, 1);
}
.gx-series__card-icon ::v-deep svg {
  transform: scale(.78);
}
.gx-series__card-icon ::v-deep .gx-series__brand-logo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
  display: block;
}
.gx-series__card-name {
  display: block;
  font-family: 'Noto Serif SC', serif;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 3px;
  transition: color .3s;
}
.gx-series__card-sub {
  display: block;
  font-size: 11px;
  color: rgba(220, 215, 240, .55);
  line-height: 1.5;
}
.gx-series__card-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #D4A946;
  margin: 8px auto 0;
  box-shadow: 0 0 7px #D4A946;
  animation: gx-pulse 1.8s ease-in-out infinite;
}
.gx-series__panel {
  border-radius: 28px;
  overflow: hidden;
  margin-bottom: 20px;
  background: linear-gradient(145deg, rgba(255, 255, 255, .09) 0%, rgba(255, 255, 255, .04) 100%);
  border: 1.5px solid;
  backdrop-filter: blur(28px);
  -webkit-backdrop-filter: blur(28px);
  position: relative;
  animation: gx-panelSlide .5s both;
  display: flex;
  min-height: 240px;
}
.gx-series__panel-beam {
  position: absolute;
  top: 0;
  left: 15%;
  right: 15%;
  height: 2px;
  border-radius: 1px;
}
.gx-series__panel-left {
  flex: 0 0 260px;
  padding: 44px 36px;
  border-right: 1px solid rgba(255, 255, 255, .06);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
}
.gx-series__badge {
  display: inline-flex;
  padding: 3px 12px;
  border-radius: 20px;
  font-family: Inter, monospace;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.5px;
  width: fit-content;
}
.gx-series__panel-icon {
  width: 84px;
  height: 84px;
  border-radius: 22px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: gx-iconFloat 4s ease-in-out infinite;
}
.gx-series__panel-icon ::v-deep .gx-series__brand-logo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
  display: block;
}
.gx-series__panel-name {
  font-family: 'Noto Serif SC', serif;
  font-size: 30px;
  font-weight: 700;
  color: #FFFFFF;
  margin-bottom: 6px;
}
.gx-series__panel-sub {
  font-size: 14px;
  color: rgba(220, 215, 240, .65);
  font-weight: 600;
}
.gx-series__panel-right {
  flex: 1;
  padding: 44px 44px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 18px;
}
.gx-series__panel-desc {
  font-size: 16px;
  color: rgba(240, 235, 250, .8);
  line-height: 1.85;
  margin: 0;
  max-width: 520px;
}
.gx-series__tags {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.gx-series__tag {
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid;
}
.gx-series__soon {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 22px;
  border-radius: 11px;
  background: rgba(255, 255, 255, .05);
  border: 1px solid rgba(255, 255, 255, .18);
  width: fit-content;
}
.gx-series__soon-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #8A95B0;
  animation: gx-pulse 2.4s ease-in-out infinite;
}
.gx-series__soon span:last-child {
  font-size: 14px;
  color: #8A95B0;
  letter-spacing: .04em;
}
.gx-series__enter {
  width: fit-content;
  height: 42px;
  padding: 0 28px;
  border-radius: 11px;
  cursor: pointer;
  border: none;
  font-size: 14px;
  font-weight: 700;
  color: #0D1630;
  transition: all 0.5s cubic-bezier(.22, 1, .36, 1);
}
.gx-series__enter:hover {
  transform: translateY(-2px) scale(1.03);
}
.gx-series__dots {
  display: flex;
  gap: 8px;
  justify-content: center;
}
.gx-series__dot {
  width: 6px;
  height: 6px;
  border-radius: 3px;
  border: none;
  cursor: pointer;
  background: rgba(255, 255, 255, .2);
  transition: all 0.4s cubic-bezier(.22, 1, .36, 1);
}
.gx-series__dot--on {
  width: 22px;
  background: #D4A946;
  box-shadow: 0 0 9px #D4A946;
}

/* ── 十大功能 ── */
.gx-feats {
  padding: 100px 0 80px;
  background: linear-gradient(180deg, #0D1630 0%, #070E22 45%, #0D1630 100%);
  position: relative;
  overflow: hidden;
}
.gx-feats__layout {
  display: flex;
  gap: 16px;
  align-items: stretch;
  opacity: 0;
  transform: translateY(24px);
  transition: opacity .7s cubic-bezier(.22, 1, .36, 1), transform .7s cubic-bezier(.22, 1, .36, 1);
}
.gx-feats__layout--visible {
  opacity: 1;
  transform: none;
}
.gx-feats__list {
  width: 232px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.gx-feats__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 11px;
  cursor: pointer;
  background: rgba(255, 255, 255, .03);
  border: 1px solid rgba(255, 255, 255, .07);
  border-left: 3px solid transparent;
  position: relative;
  transition: all 0.3s cubic-bezier(.22, 1, .36, 1);
}
.gx-feats__item--on {
  background: rgba(0, 200, 245, .08);
  border-color: rgba(0, 200, 245, .35);
  border-left-color: #00C8F5;
  box-shadow: 0 4px 18px rgba(0, 200, 245, .15), inset 0 1px rgba(255, 255, 255, .06);
}
.gx-feats__item--hov {
  background: rgba(255, 255, 255, .07);
  border-color: rgba(212, 169, 70, .25);
  border-left-color: rgba(212, 169, 70, .4);
}
.gx-feats__item-icon {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  flex-shrink: 0;
  background: rgba(212, 169, 70, .07);
  border: 1px solid rgba(212, 169, 70, .18);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(.22, 1, .36, 1);
}
.gx-feats__item-icon ::v-deep svg {
  transform: scale(.75);
}
.gx-feats__item-icon--on {
  background: rgba(0, 200, 245, .2);
  border-color: rgba(0, 200, 245, .5);
  box-shadow: 0 0 14px rgba(0, 200, 245, .35);
  transform: scale(1.08);
}
.gx-feats__item-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.gx-feats__item-name {
  font-family: 'Noto Serif SC', serif;
  font-size: 13px;
  font-weight: 700;
  color: #F0EBE0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color .25s;
}
.gx-feats__item-name--on { color: #00C8F5; }
.gx-feats__item-sub {
  font-size: 10px;
  color: #8A95B0;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.gx-feats__item-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #00C8F5;
  flex-shrink: 0;
  box-shadow: 0 0 6px #00C8F5;
  animation: gx-pulse 1.8s ease-in-out infinite;
}
.gx-feats__item-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  border-radius: 0 0 11px 11px;
  overflow: hidden;
}
.gx-feats__item-progress-fill {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #00C8F5, rgba(0, 200, 245, .5));
  transition: width .08s linear;
  box-shadow: 0 0 4px #00C8F5;
}
.gx-feats__panel {
  flex: 1;
  border-radius: 20px;
  border: 1px solid rgba(0, 200, 245, .2);
  background: rgba(255, 255, 255, .055);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  box-shadow: 0 8px 52px rgba(0, 0, 0, .45), inset 0 1px rgba(255, 255, 255, .07);
  overflow: hidden;
  animation: gx-panelSlide .35s cubic-bezier(.22, 1, .36, 1) both;
  display: flex;
  flex-direction: column;
}
.gx-feats__panel-head {
  padding: 20px 26px;
  border-bottom: 1px solid rgba(255, 255, 255, .07);
  display: flex;
  align-items: center;
  gap: 14px;
  background: rgba(0, 200, 245, .04);
}
.gx-feats__panel-icon {
  width: 46px;
  height: 46px;
  border-radius: 13px;
  flex-shrink: 0;
  background: rgba(0, 200, 245, .18);
  border: 1px solid rgba(0, 200, 245, .4);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 20px rgba(0, 200, 245, .35);
  animation: gx-iconFloat 4s ease-in-out infinite;
}
.gx-feats__panel-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.gx-feats__panel-name {
  font-family: 'Noto Serif SC', serif;
  font-size: 22px;
  font-weight: 700;
  color: #F0EBE0;
  line-height: 1.2;
}
.gx-feats__panel-sub {
  font-size: 13px;
  color: rgba(200, 192, 224, .6);
  margin-top: 2px;
}
.gx-feats__panel-live {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border-radius: 20px;
  background: rgba(0, 200, 245, .1);
  border: 1px solid rgba(0, 200, 245, .25);
  font-family: Inter, monospace;
  font-size: 10px;
  color: #00C8F5;
  letter-spacing: 1px;
  flex-shrink: 0;
}
.gx-feats__panel-live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #00C8F5;
  animation: gx-pulse 2s infinite;
}
.gx-feats__panel-body-wrap {
  flex: 1;
  display: flex;
  min-height: 280px;
}
.gx-feats__pts {
  flex: 0 0 240px;
  padding: 20px 22px;
  border-right: 1px solid rgba(255, 255, 255, .07);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 9px;
}
.gx-feats__pts-label {
  font-size: 11px;
  color: #8A95B0;
  letter-spacing: 1.5px;
  margin-bottom: 4px;
  text-transform: uppercase;
}
.gx-feats__pt {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 11px;
  border-radius: 10px;
  background: rgba(255, 255, 255, .04);
  border: 1px solid rgba(212, 169, 70, .12);
}
.gx-feats__pt-check {
  width: 17px;
  height: 17px;
  border-radius: 50%;
  flex-shrink: 0;
  background: rgba(212, 169, 70, .15);
  border: 1px solid rgba(212, 169, 70, .45);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  color: #D4A946;
}
.gx-feats__pt-text {
  font-size: 12px;
  color: rgba(215, 208, 230, .88);
  line-height: 1.4;
}
.gx-feats__preview {
  flex: 1;
  padding: 20px 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, .1);
}
.gx-feats__preview > * {
  width: 100%;
}

/* ── 星人在成长 ── */
.gx-community {
  padding: 140px 0 130px;
  background: linear-gradient(180deg, #0D1630 0%, #152248 45%, #0D1630 100%);
  position: relative;
  overflow: hidden;
}
.gx-community__journey {
  display: flex;
  margin-bottom: 52px;
}
.gx-community__stage {
  flex: 1;
  display: flex;
  align-items: stretch;
  position: relative;
}
.gx-community__stage-card {
  flex: 1;
  padding: 22px 20px;
  border-radius: 16px;
  border: 1.5px solid;
  text-align: center;
  margin: 0 6px;
  transition: all 0.5s cubic-bezier(.22, 1, .36, 1);
}
.gx-community__stage-icon {
  font-size: 28px;
  margin-bottom: 8px;
  transition: filter .5s;
}
.gx-community__stage-name {
  font-family: 'Noto Serif SC', serif;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 4px;
  transition: color 0.5s cubic-bezier(.22, 1, .36, 1);
}
.gx-community__stage-day {
  font-family: Inter, monospace;
  font-size: 9px;
  margin-bottom: 6px;
  letter-spacing: 1px;
  transition: color 0.5s cubic-bezier(.22, 1, .36, 1);
}
.gx-community__stage-metric {
  font-size: 11px;
  font-weight: 600;
  transition: color 0.5s cubic-bezier(.22, 1, .36, 1);
}
.gx-community__stage-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin: 10px auto 0;
  animation: gx-pulse 1.5s infinite;
}
.gx-community__stage-arrow {
  position: absolute;
  right: -6px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;
  font-size: 14px;
  transition: color 0.5s cubic-bezier(.22, 1, .36, 1);
}
.gx-community__split {
  display: flex;
  gap: 28px;
  margin-bottom: 44px;
  align-items: flex-start;
}
.gx-community__chart {
  flex: 1;
  min-width: 0;
}
.gx-community__chart-label {
  font-size: 12px;
  color: #8A95B0;
  margin-bottom: 8px;
}
.gx-community__feed {
  width: 280px;
  flex-shrink: 0;
}
.gx-community__feed-head {
  font-size: 12px;
  color: #8A95B0;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.gx-community__feed-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4CAF7A;
  animation: gx-pulse 2s infinite;
}
.gx-community__feed-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.gx-community__feed-item {
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, .05);
  border: 1px solid rgba(212, 169, 70, .15);
}
.gx-community__feed-top {
  display: flex;
  justify-content: space-between;
  margin-bottom: 3px;
}
.gx-community__feed-store {
  font-size: 11px;
  color: #F0EBE0;
  font-weight: 600;
}
.gx-community__feed-green {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4CAF7A;
  margin-top: 3px;
  flex-shrink: 0;
  box-shadow: 0 0 5px #4CAF7A;
}
.gx-community__feed-action {
  font-size: 10px;
  color: #8A95B0;
  margin-bottom: 3px;
}
.gx-community__feed-result {
  font-family: Inter, monospace;
  font-size: 10px;
  color: #D4A946;
}
.gx-community__stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}
.gx-community__stat {
  padding: 40px 24px 36px;
  border-radius: 24px;
  cursor: default;
  text-align: center;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(32px);
  -webkit-backdrop-filter: blur(32px);
  border: 1px solid;
}
.gx-community__stat-p1 {
  position: absolute;
  top: 16px;
  right: 18px;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  opacity: .25;
  transition: opacity .4s;
}
.gx-community__stat--hov .gx-community__stat-p1 { opacity: .7; }
.gx-community__stat-p2 {
  position: absolute;
  top: 26px;
  right: 28px;
  width: 2px;
  height: 2px;
  border-radius: 50%;
  opacity: .15;
  transition: opacity .4s;
}
.gx-community__stat--hov .gx-community__stat-p2 { opacity: .45; }
.gx-community__stat-p3 {
  position: absolute;
  bottom: 22px;
  left: 16px;
  width: 2px;
  height: 2px;
  border-radius: 50%;
  opacity: .12;
  transition: opacity .4s;
}
.gx-community__stat--hov .gx-community__stat-p3 { opacity: .4; }
.gx-community__stat-beam {
  position: absolute;
  top: 0;
  left: 20%;
  right: 20%;
  height: 1px;
  opacity: .5;
  transition: opacity .4s;
}
.gx-community__stat--hov .gx-community__stat-beam { opacity: 1; }
.gx-community__stat-orb {
  position: relative;
  width: 72px;
  height: 72px;
  margin: 0 auto 22px;
}
.gx-community__stat-ring {
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  border: 1px solid;
  animation: gx-agentSpin 6s linear infinite;
  opacity: .6;
  transition: opacity .4s;
}
.gx-community__stat--hov .gx-community__stat-ring { opacity: 1; }
.gx-community__stat-ring-inner {
  position: absolute;
  inset: 6px;
  border-radius: 50%;
  border: 1px dashed;
  animation: gx-agentSpin 10s linear infinite reverse;
}
.gx-community__stat-icon {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1.5px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.4s cubic-bezier(.22, 1, .36, 1);
}
.gx-community__stat-num {
  font-family: 'JetBrains Mono', Inter, monospace;
  font-size: 42px;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 12px;
  letter-spacing: -.03em;
  -webkit-background-clip: text;
  background-clip: text;
  animation: gx-shimmer 3s linear infinite;
  transition: filter .3s;
}
.gx-community__stat-divider {
  width: 32px;
  height: 1px;
  margin: 0 auto 12px;
}
.gx-community__stat-label {
  font-family: 'Noto Serif SC', serif;
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 5px;
  letter-spacing: .06em;
  transition: color .3s;
}
.gx-community__stat-sub {
  font-size: 11px;
  letter-spacing: .04em;
  transition: color .3s;
}

@media (max-width: 900px) {
  .gx-section-inner {
    padding: 0 24px;
  }
  .gx-series__cards {
    grid-template-columns: repeat(2, 1fr);
  }
  .gx-series__panel {
    flex-direction: column;
  }
  .gx-series__panel-left {
    flex: none;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, .06);
  }
  .gx-feats__layout {
    flex-direction: column;
  }
  .gx-feats__list {
    width: 100%;
  }
  .gx-feats__panel-body-wrap {
    flex-direction: column;
  }
  .gx-feats__pts {
    flex: none;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, .07);
  }
  .gx-community__split {
    flex-direction: column;
  }
  .gx-community__feed {
    width: 100%;
  }
  .gx-community__stats {
    grid-template-columns: repeat(2, 1fr);
  }
  .gx-community__journey {
    flex-direction: column;
    gap: 10px;
  }
}
</style>
